import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ExternalLink, Sparkles } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import LegalDisclaimer from '../components/LegalDisclaimer';
import VideoStrip from './courses/VideoStrip';
import { coursesAPI } from '../services/api';

const ArticleLink = ({ article }) => {
  if (!article || !article.url) return null;
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 border border-purple-200 text-sm text-purple-900"
    >
      <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span className="flex-1">
        <span className="block font-medium">{article.title}</span>
        {article.publisher && (
          <span className="text-xs text-purple-700">{article.publisher}</span>
        )}
      </span>
      <ExternalLink className="w-3.5 h-3.5 mt-1" />
    </a>
  );
};

/**
 * Public read-only view of a shared AI course.
 *
 * The page picks up the sharer's referral code from the API response and
 * exposes a prominent "Create your own free course" CTA that links to
 * /auth?mode=signup&ref=CODE — every new signup attributes to the sharer.
 */
const SharedCoursePage = () => {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await coursesAPI.getShared(token);
      setDoc(data);
    } catch (e) {
      const status = e.response?.status;
      setError(status === 404 ? 'This share link has been revoked or never existed.' : 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Tell crawlers (Google/Bing/Twitter etc) NOT to index public share pages.
   * Reasons:
   *   1. We don't want user-generated course content competing with our own
   *      marketing pages in SERPs.
   *   2. AI-generated text is treated as low-quality by Google → SEO hit.
   *   3. Some courses contain the sharer's referral code — we don't want
   *      Googlebot caching it across unrelated SERPs.
   *
   * We add the tag dynamically (SPA shell doesn't know which route we're on
   * at HTML response time) and clean up on unmount so other pages don't
   * inherit it.
   */
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      try {
        document.head.removeChild(meta);
      } catch (_) {
        /* node already removed by another effect */
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-4xl mx-auto px-4 text-gray-500">Loading shared course…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Link unavailable</h1>
          <p className="mt-3 text-gray-600">{error}</p>
          <Link to="/courses" className="mt-6 inline-block px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium">
            Create your own free course
          </Link>
        </div>
      </div>
    );
  }
  if (!doc) return null;

  const course = doc.course || {};
  const refCode = doc.referral_code;
  const signupHref = refCode
    ? `/auth?mode=signup&ref=${encodeURIComponent(refCode)}`
    : '/auth?mode=signup';

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 hover:text-purple-600"
          data-testid="shared-back-home"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Neon Alpha home
        </Link>

        <Card className="bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] border-purple-500/20 text-white">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[280px]">
                <Badge className="bg-purple-600/30 text-purple-200 border border-purple-500/30">
                  {doc.mode === 'quick' ? 'Quick search' : 'Full course'} · Shared
                </Badge>
                <h1
                  className="text-2xl md:text-3xl font-bold mt-2"
                  data-testid="shared-course-title"
                >
                  {course.title || doc.topic}
                </h1>
                <p className="text-sm text-gray-300 mt-1">Topic: {doc.topic}</p>
              </div>
              <Link to={signupHref}>
                <Button
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                  data-testid="shared-signup-cta"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Create your own free course
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Body */}
        {doc.mode === 'quick' ? (
          <>
            {course.summary && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-900">Overview</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700 whitespace-pre-line">
                  {course.summary}
                </CardContent>
              </Card>
            )}
            {Array.isArray(course.key_takeaways) && course.key_takeaways.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-900">Key takeaways</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
                    {course.key_takeaways.map((k) => (
                      <li key={k}>{k}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-gray-900">YouTube videos</CardTitle>
              </CardHeader>
              <CardContent>
                <VideoStrip videos={course.videos} testIdPrefix="shared-video" />
              </CardContent>
            </Card>
            {Array.isArray(course.articles) && course.articles.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-900">Curated articles</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {course.articles.map((a, idx) => (
                    <ArticleLink key={a.url || idx} article={a} />
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
            {course.overview && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-900">Overview</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-gray-700 whitespace-pre-line">
                  {course.overview}
                </CardContent>
              </Card>
            )}
            {(course.modules || []).map((mod, mi) => (
              <Card key={mod.title || `module-${mi}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">
                        Module {mi + 1}
                      </p>
                      <CardTitle className="text-gray-900">{mod.title}</CardTitle>
                      {mod.objective && (
                        <p className="text-sm text-gray-600 mt-1">{mod.objective}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {(mod.lessons || []).length} lessons
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(mod.lessons || []).map((lesson) => (
                    <div
                      key={lesson.id}
                      className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
                    >
                      <p className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">
                        Module {mi + 1} · Lesson {lesson.id?.replace(/^m\d+l/, '')}
                      </p>
                      <h4 className="text-base font-semibold text-gray-900">{lesson.title}</h4>
                      {lesson.summary && (
                        <p className="text-sm text-gray-700 whitespace-pre-line">{lesson.summary}</p>
                      )}
                      {Array.isArray(lesson.key_takeaways) && lesson.key_takeaways.length > 0 && (
                        <ul className="list-disc pl-5 space-y-1 text-xs text-gray-600">
                          {lesson.key_takeaways.map((k) => (
                            <li key={k}>{k}</li>
                          ))}
                        </ul>
                      )}
                      <VideoStrip videos={lesson.videos} testIdPrefix={`shared-lesson-${lesson.id}`} />
                      <ArticleLink article={lesson.article} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {/* Bottom CTA */}
        <Card className="bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-200">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-bold text-gray-900">Want your own AI-built course?</h3>
            <p className="text-sm text-gray-600 mt-1">
              Generate one for free every month — pick any investing topic.
            </p>
            <Link to={signupHref}>
              <Button
                className="mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="shared-signup-cta-bottom"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Start free on Neon Alpha
              </Button>
            </Link>
          </CardContent>
        </Card>

        <LegalDisclaimer variant="general" />
      </div>
    </div>
  );
};

export default SharedCoursePage;
